// ShareHubCard — founder's one-stop share launcher.
// Instead of remembering 6 URLs + 6 scripts, pick an audience and get a ready-
// to-paste message + the correct URL in one click. Mints beta codes on demand.
import { useCallback, useEffect, useRef, useState } from "react";
import { Share2, Copy, Check, Loader2, Users, GraduationCap, Heart, Rocket, Sparkles, Key, Send } from "lucide-react";
import { generateBetaCodes } from "@/lib/api";
import { toast } from "sonner";

const AUDIENCES = [
  {
    key: "friend",
    label: "Friend or family",
    icon: Heart,
    needsCode: false,
    url: () => "https://getlectura.app",
    message: (link, firstName = "") => (
`Hey${firstName ? ` ${firstName}` : ""} 👋

I've been quietly building Lectura — type a book + chapter, get a full narrated lecture, slides, printable study guide, and a graded quiz back in under 2 minutes. Free tier is 3 lectures, no credit card.

Would love your honest feedback if you want to try it:

${link}

Thanks for being a friend who actually tries my stuff 💚
— Nicole`
    ),
  },
  {
    key: "beta",
    label: "Beta tester (lifetime Pro code)",
    icon: Key,
    needsCode: true,
    url: (code) => `https://getlectura.app/register?code=${code}`,
    message: (link, firstName = "") => (
`Hey${firstName ? ` ${firstName}` : ""} —

Giving you a founding-tester lifetime Pro code for Lectura, the AI study tool I've been building. One tap, no credit card, no expiry:

${link}

If you try it, I'd love one honest line about what worked (or didn't). That's the whole ask. Thanks for being one of my first testers 💚
— Nicole`
    ),
  },
  {
    key: "teacher",
    label: "Teacher (founding program)",
    icon: GraduationCap,
    needsCode: false,
    url: () => "https://getlectura.app/teachers-wanted",
    message: (link, firstName = "") => (
`Hi${firstName ? ` ${firstName}` : ""} —

I'm Nicole, the founder of Lectura — an AI study tool that turns any textbook chapter into a narrated lecture, slides, printable study guide, and a graded quiz. It's FERPA-Ready + COPPA-Compliant for K-12.

I'm offering free Pro forever to 10 founding teachers in exchange for honest feedback and (optional) a quote. Takes 30 seconds to apply:

${link}

No strings, no card. I'd love to hear if this fits your classroom.
— Nicole`
    ),
  },
  {
    key: "student_group",
    label: "A whole study group (8 seats, 14-day Pro each)",
    icon: Users,
    needsCode: false,
    url: () => "/#/dashboard-studygroup", // hint to go to Study Group card
    message: () =>
`To create a study group link:
1. Scroll to the "Study group invite" card on your dashboard
2. Name the group ("AP Bio squad" etc)
3. Click Create → copy the link
4. Share with up to 8 friends — each gets 14 days of Pro, no card

That link is unique per group so you can hand out multiple.`,
    isHint: true,
  },
  {
    key: "launch",
    label: "Launch-day waitlist (Product Hunt)",
    icon: Rocket,
    needsCode: false,
    url: () => "https://getlectura.app/launch",
    message: (link) => (
`Lectura is launching on Product Hunt soon — first 24 hours get 50% off Pro.

Drop your email on the launch page and I'll ping you the exact moment we go live, plus your discount code:

${link}

No spam, one email.
— Nicole`
    ),
  },
];

export const ShareHubCard = ({ role }) => {
  const [aud, setAud] = useState("friend");
  const [firstName, setFirstName] = useState("");
  const [codeCache, setCodeCache] = useState(null);
  const [minting, setMinting] = useState(false);
  const [copied, setCopied] = useState(null);
  const [canNativeShare, setCanNativeShare] = useState(false);

  useEffect(() => {
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      setCanNativeShare(true);
    }
  }, []);

  const current = AUDIENCES.find((a) => a.key === aud);

  // Mint a fresh beta code on-demand when the user picks the beta audience.
  // Cache per-session so repeated copies don't burn new codes.
  const ensureCode = useCallback(async () => {
    if (!current.needsCode) return null;
    if (codeCache) return codeCache;
    setMinting(true);
    try {
      const label = `Share Hub${firstName ? ` → ${firstName.trim()}` : ""}`;
      const res = await generateBetaCodes(1, label);
      const code = res.codes?.[0];
      if (!code) throw new Error("Mint failed");
      setCodeCache(code);
      return code;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't mint a code.");
      return null;
    } finally {
      setMinting(false);
    }
  }, [current.needsCode, codeCache, firstName]);

  // Reset the cached code when the audience or recipient name changes so the
  // next copy mints a fresh code for the right person.
  useEffect(() => {
    setCodeCache(null);
    setCopied(null);
  }, [aud, firstName]);

  const buildLink = async () => {
    if (!current.needsCode) return current.url();
    const code = await ensureCode();
    return code ? current.url(code) : null;
  };

  const buildMessage = async () => {
    const link = await buildLink();
    if (!link && current.needsCode) return null;
    return current.message(link, firstName.trim());
  };

  const copyLink = async () => {
    const link = await buildLink();
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied("link");
      toast.success(current.needsCode ? "Beta sign-up link copied — paste it in a DM" : "Link copied");
      setTimeout(() => setCopied(null), 2200);
    } catch { toast.error("Couldn't copy"); }
  };

  const copyMessage = async () => {
    const msg = await buildMessage();
    if (!msg) return;
    try {
      await navigator.clipboard.writeText(msg);
      setCopied("message");
      toast.success("Message + link copied — paste anywhere");
      setTimeout(() => setCopied(null), 2200);
    } catch { toast.error("Couldn't copy"); }
  };

  const nativeShare = async () => {
    const msg = await buildMessage();
    if (!msg) return;
    try {
      if (canNativeShare) {
        await navigator.share({ text: msg, title: "Lectura" });
      } else {
        copyMessage();
      }
    } catch { /* user cancelled */ }
  };

  if (role !== "admin") return null;

  const Icon = current.icon;

  return (
    <div data-testid="share-hub-card" className="rounded-2xl border border-border bg-card p-6">
      <div className="flex items-start gap-3">
        <span className="grid place-items-center h-10 w-10 rounded-xl bg-primary text-primary-foreground shrink-0">
          <Share2 className="h-5 w-5" />
        </span>
        <div className="flex-1">
          <h3 className="font-serif text-xl font-bold">Share Lectura</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Pick who you're inviting — I'll write the message and pick the right link. One-click copy, ready to paste anywhere.
          </p>
        </div>
      </div>

      {/* Audience picker */}
      <div className="mt-5 grid gap-2 sm:grid-cols-2">
        {AUDIENCES.map((a) => {
          const A = a.icon;
          return (
            <button
              key={a.key}
              data-testid={`share-hub-aud-${a.key}`}
              onClick={() => setAud(a.key)}
              className={`flex items-center gap-2 rounded-xl border-2 px-3 py-2.5 text-sm font-semibold text-left transition ${
                aud === a.key ? "border-accent bg-accent/10 text-accent" : "border-border hover:bg-secondary/60"
              }`}
            >
              <A className="h-4 w-4 shrink-0" />
              <span className="truncate">{a.label}</span>
            </button>
          );
        })}
      </div>

      {/* First-name personalization (for 1:1 outreach) */}
      {!current.isHint && (
        <div className="mt-4">
          <label htmlFor="share-hub-name" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
            Recipient's first name <span className="font-normal normal-case text-foreground/60">(optional — makes it feel personal)</span>
          </label>
          <input
            id="share-hub-name"
            data-testid="share-hub-first-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="e.g. Sam"
            maxLength={40}
            className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm"
          />
        </div>
      )}

      {/* Hint-only audience (study group) — different UI */}
      {current.isHint ? (
        <div className="mt-4 rounded-xl border border-accent/30 bg-accent/5 p-4 text-sm whitespace-pre-wrap" data-testid="share-hub-hint">
          {current.message()}
        </div>
      ) : (
        <>
          {/* Preview */}
          <div className="mt-4">
            <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Preview</label>
            <div
              className="mt-1 rounded-xl border border-border bg-secondary/40 p-3 text-sm whitespace-pre-wrap font-mono leading-relaxed max-h-56 overflow-y-auto"
              data-testid="share-hub-preview"
            >
              {current.message(codeCache ? current.url(codeCache) : current.needsCode ? "https://getlectura.app/register?code=…" : current.url(), firstName.trim())}
            </div>
            {current.needsCode && (
              <p className="mt-1.5 text-[11px] text-muted-foreground inline-flex items-center gap-1">
                <Sparkles className="h-3 w-3 text-accent" />
                A fresh single-use lifetime code is minted the moment you copy — one code per person.
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
            <button
              data-testid="share-hub-copy-msg"
              onClick={copyMessage}
              disabled={minting}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2.5 text-sm font-bold hover:opacity-90 transition disabled:opacity-60 active:scale-[0.98]"
            >
              {minting ? <Loader2 className="h-4 w-4 animate-spin" /> : copied === "message" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied === "message" ? "Copied" : "Copy message + link"}
            </button>
            <button
              data-testid="share-hub-copy-link"
              onClick={copyLink}
              disabled={minting}
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-border px-4 py-2.5 text-sm font-semibold hover:bg-secondary/60 disabled:opacity-60 active:scale-[0.98]"
            >
              {copied === "link" ? <><Check className="h-4 w-4" /> Copied</> : <><Copy className="h-4 w-4" /> Link only</>}
            </button>
            <button
              data-testid="share-hub-native"
              onClick={nativeShare}
              disabled={minting}
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-border px-4 py-2.5 text-sm font-semibold hover:bg-secondary/60 disabled:opacity-60 active:scale-[0.98]"
              title={canNativeShare ? "Open your system share sheet (iMessage, Mail, Messenger, etc.)" : "Fallback: copies message to clipboard"}
            >
              <Send className="h-4 w-4" /> Share…
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ShareHubCard;
